import React, { useState, useEffect, useMemo } from 'react';

const OddsSelectionGrid = ({ 
  event, 
  liveOdds, 
  getContestOdds, 
  onCalculateArbitrage,
  loading,
  error 
}) => {
  const [selectedBets, setSelectedBets] = useState(new Set());
  const [gridData, setGridData] = useState([]);

  // Build grid data from event contests and live odds
  const buildGridData = useMemo(() => {
    if (!event || !event.contests || !liveOdds) return [];

    const data = [];
    
    event.contests.forEach((contest, contestIndex) => {
      if (!contest.draftkings || !contest.fanduel) return;

      const dkContestId = contest.draftkings.contest_id;
      const fdContestId = contest.fanduel.contest_id;
      
      const dkOdds = getContestOdds(dkContestId, 'DraftKings');
      const fdOdds = getContestOdds(fdContestId, 'FanDuel');

      if (dkOdds && fdOdds) {
        // Create rows for each betting selection
        const maxSelections = Math.max(dkOdds.length, fdOdds.length);
        
        for (let i = 0; i < maxSelections; i++) {
          const dkSelection = dkOdds[i];
          const fdSelection = fdOdds[i];
          
          if (dkSelection || fdSelection) {
            data.push({
              contestType: contest.match_type,
              contestIndex,
              selectionIndex: i,
              draftKings: dkSelection ? {
                label: dkSelection.label,
                odds: dkSelection.odds,
                contestId: dkContestId,
                platform: 'DraftKings',
                id: `dk-${dkContestId}-${i}`
              } : null,
              fanDuel: fdSelection ? {
                label: fdSelection.label,
                odds: fdSelection.odds,
                contestId: fdContestId,
                platform: 'FanDuel',
                id: `fd-${fdContestId}-${i}`
              } : null
            });
          }
        }
      }
    });

    return data;
  }, [event, liveOdds, getContestOdds]);

  useEffect(() => {
    setGridData(buildGridData);
    setSelectedBets(new Set()); // Clear selections when data changes
  }, [buildGridData]);

  const handleBetSelection = (betId) => {
    const newSelected = new Set(selectedBets);
    if (newSelected.has(betId)) {
      newSelected.delete(betId);
    } else {
      newSelected.add(betId);
    }
    setSelectedBets(newSelected);
  };

  const calculateSelectedArbitrage = () => {
    const selectedBetData = [];
    
    gridData.forEach(row => {
      if (row.draftKings && selectedBets.has(row.draftKings.id)) {
        selectedBetData.push(row.draftKings);
      }
      if (row.fanDuel && selectedBets.has(row.fanDuel.id)) {
        selectedBetData.push(row.fanDuel);
      }
    });

    onCalculateArbitrage(selectedBetData);
  };

  const clearAllSelections = () => {
    setSelectedBets(new Set());
  };

  if (!event || !liveOdds) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '6px',
        color: '#6c757d'
      }}>
        Select an event and fetch odds to see the selection grid
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        backgroundColor: '#f8f9fa', 
        borderRadius: '6px'
      }}>
        Loading odds data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        backgroundColor: '#f8d7da', 
        borderRadius: '6px',
        color: '#721c24'
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '15px' 
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1em', color: '#333' }}>
          Arbitrage Selection Grid
        </h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={clearAllSelections}
            disabled={selectedBets.size === 0}
            style={{
              padding: '6px 12px',
              backgroundColor: selectedBets.size === 0 ? '#6c757d' : '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedBets.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.8em'
            }}
          >
            Clear All ({selectedBets.size})
          </button>
          <button
            onClick={calculateSelectedArbitrage}
            disabled={selectedBets.size < 2}
            style={{
              padding: '6px 12px',
              backgroundColor: selectedBets.size < 2 ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedBets.size < 2 ? 'not-allowed' : 'pointer',
              fontSize: '0.8em',
              fontWeight: 'bold'
            }}
          >
            Calculate Arbitrage ({selectedBets.size} selected)
          </button>
        </div>
      </div>

      <div style={{ 
        fontSize: '0.8em', 
        color: '#6c757d', 
        marginBottom: '10px',
        padding: '8px',
        backgroundColor: '#e9ecef',
        borderRadius: '4px'
      }}>
        💡 Select betting options by clicking the dots. Choose at least 2 options to calculate arbitrage opportunities.
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '6px',
          overflow: 'hidden',
          border: '1px solid #dee2e6'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{
                padding: '10px',
                textAlign: 'left',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
                color: '#495057',
                fontSize: '0.85em'
              }}>
                Contest Type
              </th>
              <th style={{
                padding: '10px',
                textAlign: 'center',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
                color: '#495057',
                fontSize: '0.85em'
              }}>
                DraftKings
              </th>
              <th style={{
                padding: '10px',
                textAlign: 'center',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
                color: '#495057',
                fontSize: '0.85em'
              }}>
                FanDuel
              </th>
            </tr>
          </thead>
          <tbody>
            {gridData.map((row, index) => (
              <tr key={index} style={{
                borderBottom: '1px solid #dee2e6',
                '&:hover': { backgroundColor: '#f8f9fa' }
              }}>
                <td style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '0.8em',
                  backgroundColor: '#f8f9fa'
                }}>
                  {row.contestType}
                </td>
                <td style={{
                  padding: '12px',
                  textAlign: 'center'
                }}>
                  {row.draftKings ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      <button
                        onClick={() => handleBetSelection(row.draftKings.id)}
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          border: '2px solid #007bff',
                          backgroundColor: selectedBets.has(row.draftKings.id) ? '#007bff' : 'white',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {selectedBets.has(row.draftKings.id) && (
                          <span style={{ color: 'white', fontSize: '10px' }}>✓</span>
                        )}
                      </button>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75em'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#007bff' }}>
                          {row.draftKings.odds}
                        </div>
                        <div style={{ color: '#333', marginTop: '2px' }}>
                          {row.draftKings.label}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#6c757d', fontSize: '0.8em' }}>-</div>
                  )}
                </td>
                <td style={{
                  padding: '12px',
                  textAlign: 'center'
                }}>
                  {row.fanDuel ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      <button
                        onClick={() => handleBetSelection(row.fanDuel.id)}
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          border: '2px solid #6c757d',
                          backgroundColor: selectedBets.has(row.fanDuel.id) ? '#6c757d' : 'white',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {selectedBets.has(row.fanDuel.id) && (
                          <span style={{ color: 'white', fontSize: '10px' }}>✓</span>
                        )}
                      </button>
                      <div style={{
                        backgroundColor: '#f5f5f5',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75em'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#6c757d' }}>
                          {row.fanDuel.odds}
                        </div>
                        <div style={{ color: '#333', marginTop: '2px' }}>
                          {row.fanDuel.label}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#6c757d', fontSize: '0.8em' }}>-</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OddsSelectionGrid;
